import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';

const AwardsSection = styled.section`
  padding: 80px 10%;
  background-color: var(--color-bg);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 60px 5%;
  }
  
  @media (max-width: 576px) {
    padding: 40px 5%;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: var(--color-text);
  margin-bottom: 1rem;
  text-align: center;
  position: relative;
  font-weight: 600;
  letter-spacing: 0.5px;

  &:after {
    content: '';
    position: absolute;
    width: 50px;
    height: 3px;
    background-color: var(--color-primary);
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }

  @media (max-width: 576px) {
    font-size: 1.8rem;
  }
`;

const AwardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.2rem;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const AwardCard = styled(motion.div)`
  background: var(--color-card);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 15px var(--color-shadow);
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 30px var(--color-shadow);
    border-color: var(--color-primary);
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  @media (max-width: 576px) {
    padding: 1.2rem;
  }
`;

const AwardIcon = styled(motion.div)`
  width: 60px;
  height: 60px;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, var(--color-primary), var(--color-secondary));
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: white;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  
  @media (max-width: 576px) {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
    margin-bottom: 0.8rem;
  }
`;

const AwardTitle = styled.h3`
  font-size: 1.1rem;
  color: var(--color-text);
  margin-bottom: 0.5rem;
  font-weight: 600;
  line-height: 1.3;
  
  @media (max-width: 576px) {
    font-size: 1rem;
  }
`;

const AwardIssuer = styled.p`
  font-size: 0.9rem;
  color: var(--color-primary);
  margin-bottom: 0.5rem;
  font-weight: 500;
  
  @media (max-width: 576px) {
    font-size: 0.85rem;
  }
`;

const AwardDate = styled.p`
  font-size: 0.8rem;
  color: var(--color-secondary);
  margin: 0;
  font-weight: 400;
  
  @media (max-width: 576px) {
    font-size: 0.75rem;
  }
`;

const Awards = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, threshold: 0.1 });

  const awardsData = [
    {
      id: 1,
      title: 'Innovation Award',
      issuer: 'Apple Inc',
      date: 'October 2016',
      icon: "🍎"
    },
    {
      id: 2,
      title: 'Rising Star Award',
      issuer: 'Broadcom Inc',
      date: '2010 & 2011',
      icon: "⭐"
    },
    {
      id: 3,
      title: 'Guest Lecturer Recognition',
      issuer: 'BITS, Pilani',
      date: 'January 2017',
      icon: "🎓"
    },
    {
      id: 4,
      title: 'Student of the Year Award',
      issuer: 'Anna University Chennai',
      date: '2001',
      icon: "🏆"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.8,
      rotateY: -15
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      rotateY: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        type: "spring",
        stiffness: 100
      }
    }
  };

  const iconVariants = {
    hidden: { 
      scale: 0,
      rotate: -180
    },
    visible: {
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2
      }
    }
  };

  return (
    <AwardsSection id="awards" ref={sectionRef}>
      <SectionTitle>Awards & Recognition</SectionTitle>
      <br />
      <AwardsGrid
        as={motion.div}
        variants={containerVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
      >
        {awardsData.map((award) => (
          <AwardCard
            key={award.id}
            variants={cardVariants}
            whileHover={{ 
              scale: 1.05,
              rotateY: 5,
              transition: { duration: 0.2 }
            }}
            whileTap={{ scale: 0.95 }}
          >
            <AwardIcon
              variants={iconVariants}
              whileHover={{ 
                rotate: 360,
                scale: 1.1,
                transition: { duration: 0.5 }
              }}
            >
              {award.icon}
            </AwardIcon>
            <AwardTitle>{award.title}</AwardTitle>
            <AwardIssuer>{award.issuer}</AwardIssuer>
            <AwardDate>{award.date}</AwardDate>
          </AwardCard>
        ))}
      </AwardsGrid>
    </AwardsSection>
  );
};

export default Awards;